/* eslint-disable react-refresh/only-export-components */
import { ReactNode, useEffect } from 'react';

import Cookies from 'js-cookie';

import { getConfig } from '@/config';
import { clearPermissionOverridesStorage } from '@/lib/auth/permission-overrides-storage';
import { setDebugFlag, useFlagsStore } from '@/lib/client-store';
import { clearAmplitudeFeatureFlagOverridesStorage } from '@/lib/feature-flags/url-and-storage';
import { useIsSystemAdmin } from '@/lib/hooks/use-is-system-admin';

import {
  getDebugFromCookie,
  setDebugCookie,
  VERSION_COOKIE_NAME,
} from './debug-state';

interface VersionInfo {
  version: string | null;
  environment: string;
  isPreview: boolean;
}

/**
 * Hook to manage version and environment information
 * @returns VersionInfo object containing version, environment, and preview status
 */
export const useVersion = (): VersionInfo => {
  const { environment } = getConfig();
  const version = Cookies.get(VERSION_COOKIE_NAME) ?? null;
  const isPreview = !!version;

  return { version, environment, isPreview };
};

/**
 * Hook to manage basic debug state without admin permissions
 * @returns [boolean, (enabled: boolean) => void] tuple containing debug state and setter
 */
export const useDebugState = (): [boolean, (enabled: boolean) => void] => {
  const isDebugEnabled = useFlagsStore((s) => s.debug);

  // Initialize state from cookie on mount
  useEffect(() => {
    if (getDebugFromCookie()) {
      setDebugFlag(true);
    }
  }, []);

  const setDebug = (enabled: boolean) => {
    setDebugCookie(enabled);
    setDebugFlag(enabled);
  };

  return [isDebugEnabled, setDebug];
};

export const useDebug = (
  _context?: string
): [boolean, (enabled: boolean) => void] => {
  // Mark optional parameter as used for linting purposes
  void _context;
  const isSystemAdmin = useIsSystemAdmin();
  const [isDebugEnabled, setDebug] = useDebugState();

  const setAdminDebug = (enabled: boolean) => {
    // Only allow system admins to set debug mode
    if (!isSystemAdmin) {
      return;
    }
    if (!enabled) {
      clearAmplitudeFeatureFlagOverridesStorage();
      clearPermissionOverridesStorage();
    }
    setDebug(enabled);
  };

  return [isDebugEnabled, setAdminDebug];
};

interface DebugProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Component that conditionally renders its children based on debug mode
 * @param props.children - Content to render when debug mode is enabled
 * @param props.fallback - Optional content to render when debug mode is disabled
 */
export const Debug = ({ children, fallback = null }: DebugProps) => {
  const [isDebugEnabled] = useDebugState();
  return isDebugEnabled ? <>{children}</> : <>{fallback}</>;
};
